/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { cloneDeep } from "lodash";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaInfo, FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useSetRecoilState } from "recoil";
import { testWebhook } from "../../../../api/Campaign";
import { ContactApi } from "../../../../api/Contacts";
import { updateUser } from "../../../../api/User";
import { ContactFiltersAtom } from "../../../../atoms/ContactFiltersAtom";
import { UserAtom } from "../../../../atoms/UserAtom";
import { IUserIRoute } from "../../../../generated/Api";
import { FilterByEmailAtom } from "../../../../pages/contacts";
import { INode, IWebHook } from "../../../../types/CampaignTypes";
import { IOption } from "../../../../types/CommonTypes";
import { validationEmail } from "../../../../utils/Validation";
import AddContact from "../../../Modal/container/AddContact";
import Warning from "../../../Modal/container/Warning";
import { useModal } from "../../../Modal/Modal";
import StyledAutocompleteSelect from "../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../../StyledButton";
import { validateWebhook } from "../Form/Webhook";

interface TestPopoverType {
  position: { top: number; left: number };
}

const TestPopover = styled.div<TestPopoverType>`
  position: fixed;
  /* bottom: -115px;
  left: -40px; */
  height: 120px;
  background-color: white;
  padding: 10px;
  color: #2f2f2f;
  width: 350px;
  text-align: left;
  border-radius: 0px 5px 5px 5px;
  border: 1px solid #ddd;
  z-index: 999;

  ${({ position: { left, top } }) =>
    css`
      right: 0;
      top: ${top}px;
      left: ${left}px;
      bottom: auto;
    `}
`;

const IconButton = styled.div`
  height: 32px;
  width: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;

  border: 1px solid #dddddd;

  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ItemToolbarContainer = styled.div`
  display: flex;
  grid-gap: 8px;

  position: absolute;
  right: 10px;

  align-items: center;
  margin-top: -6px;
`;

const IconButtonWithHover = styled(IconButton)`
  color: #aaaaaa !important;

  &:hover {
    color: #fb972e !important;
  }
`;

export const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

interface WebhookTestPopoverProps {
  webhook: INode;
}

const WebhookTestPopover = ({ webhook }: WebhookTestPopoverProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setContactFilters = useSetRecoilState(ContactFiltersAtom);
  const setFilterByEmail = useSetRecoilState(FilterByEmailAtom);

  const [user, setUser] = useRecoilState(UserAtom);
  const router = useRouter();

  const modal = useModal();

  // // As close to server code as possible
  // const style = body.split('pre style="')[1]?.split('"')[0] || null;

  const [selectedValue, setSelectedValue] = useState(
    user.options.testEmailRecipient && user.options.testEmailRecipient[0]
      ? user.options.testEmailRecipient[0]
      : ""
  );
  const [testEmailOption, setTestEmailOption] = useState<IOption>(
    user.options.testEmailRecipient && user.options.testEmailRecipient[0]
      ? {
          name: user.options.testEmailRecipient[0],
          value: user.options.testEmailRecipient[0],
        }
      : {
          name: "",
          value: "",
        }
  );

  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocationSet, setIsLocationSet] = useState(false);

  const [dropViewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });

  function updateDropViewPosition() {
    if (!containerRef.current) return;
    const elementBoundingRect = (
      containerRef.current as HTMLDivElement
    )?.parentElement?.getBoundingClientRect();

    if (!elementBoundingRect) return;

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });
    setIsLocationSet(true);
  }

  useEffect(() => {
    if (!containerRef.current) return;
    updateDropViewPosition();

    window.addEventListener("resize", updateDropViewPosition);
    window.addEventListener("wheel", updateDropViewPosition);

    return () => {
      window.removeEventListener("resize", updateDropViewPosition);
      window.removeEventListener("wheel", updateDropViewPosition);
    };
  }, []);

  const options = useMemo(
    () =>
      user.options.testEmailRecipient && user.options.testEmailRecipient.map
        ? user.options.testEmailRecipient.map((e) => ({
            name: e,
            value: e,
          }))
        : [],
    [user.options.testEmailRecipient]
  );

  useEffect(() => {
    let isEmailValid = validationEmail(selectedValue);

    setIsValidEmail(isEmailValid);
  }, [testEmailOption, selectedValue]);

  async function handleCanSendTestEmail(email: string) {
    const { contacts, total } = await ContactApi.read({
      limit: 1,
      search: email,
    });

    if (contacts && contacts.length > 0) return true;
    return false;
  }

  async function deleteTestEmail(email: string) {
    const updatedUser: IUserIRoute = {
      ...user,
      options: {
        ...user.options,
        testEmailRecipient: user.options.testEmailRecipient
          ? user.options.testEmailRecipient.filter((e) => e !== email)
          : [],
      },
    };

    // Put the email being sent at the front, so it's the first email
    await updateUser(updatedUser);
    setUser(updatedUser as any);

    // If it's on the input, clear it
    if (email === selectedValue) {
      setSelectedValue("");
    }
  }

  async function handleSendTestWebhook() {
    try {
      const updatedUser: IUserIRoute = {
        ...user,
        options: {
          ...user.options,
          testEmailRecipient: user.options.testEmailRecipient
            ? Array.from(
                new Set([selectedValue, ...user.options.testEmailRecipient])
              )
            : [selectedValue],
        },
      };

      // Put the email being sent at the front, so it's the first email
      await updateUser(updatedUser);
      setUser(updatedUser as any);

      let sentWebhook = cloneDeep(webhook.webHook);

      if (!sentWebhook.contentType) sentWebhook.contentType = "form-data";
      if (!sentWebhook.fields) sentWebhook.fields = [];

      await testWebhook(sentWebhook, selectedValue, user.userUid);

      // If the option is blank, then I used the value in the input to autocreate an option
      // so I should create an option here and select it
      if (testEmailOption.value !== selectedValue) {
        setTestEmailOption({
          value: selectedValue,
          name: selectedValue,
        });
      }
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  }

  return (
    <TestPopover
      position={dropViewPosition}
      ref={containerRef}
      css={css`
        opacity: ${!isLocationSet ? 0 : 1};
      `}
    >
      <StyledAutocompleteSelect
        hideNoOption
        selectedOption={testEmailOption}
        onOptionSelect={(option) => {
          setTestEmailOption(option);
        }}
        placeholder="Test webhook recipient"
        selectedValue={selectedValue}
        setSelectedValue={(text) => setSelectedValue(text)}
        options={options}
        resetOnClosedDropview={false}
        removeCheckIcon
        substring={25}
        renderCustomItemToolbar={(option) => (
          <ItemToolbarContainer>
            <IconButtonWithHover
              data-tip
              data-for="info"
              onClick={() => {
                setContactFilters((oldFilter) => ({
                  ...oldFilter,
                  search: option.value.toLowerCase().trim() as string,
                }));
                setFilterByEmail(option.value.toLowerCase().trim() as string);

                modal.popModal();

                router.push(`/contacts/?search=${option.value}`);
              }}
            >
              <FaInfo />
              <ReactTooltip
                id="info"
                place="top"
                effect="solid"
                backgroundColor="#333333"
                css={css`
                  padding: 4px 8px;
                `}
              >
                <TooltipText>Contact Information</TooltipText>
              </ReactTooltip>
            </IconButtonWithHover>

            <IconButtonWithHover
              data-tip
              data-for="edit"
              onClick={() => {
                setContactFilters((oldFilter) => ({
                  ...oldFilter,
                  search: option.value.toLowerCase().trim() as string,
                }));

                setFilterByEmail(option.value.toLowerCase().trim() as string);

                modal.popModal();

                router.push(`/contacts/?search=${option.value}&modal=true`);
              }}
            >
              <FaPencilAlt />
              <ReactTooltip
                id="edit"
                place="top"
                effect="solid"
                backgroundColor="#333333"
                css={css`
                  padding: 4px 8px;
                `}
              >
                <TooltipText>Edit Contact</TooltipText>
              </ReactTooltip>
            </IconButtonWithHover>

            <IconButtonWithHover
              data-tip
              data-for="delete"
              onClick={() =>
                modal.pushModal(
                  <Warning
                    icon={"/icons/delete.svg"}
                    title="Delete Contact"
                    description={`Are you sure you want delete this contact of test list?`}
                    buttonsName={["No", "Yes"]}
                    onOkClicked={() => {
                      deleteTestEmail(option.value);
                      modal.popModal();
                    }}
                    onCancelClicked={() => {
                      modal.popModal();
                    }}
                  />
                )
              }
            >
              <FaRegTrashAlt />
              <ReactTooltip
                id="delete"
                place="top"
                effect="solid"
                backgroundColor="#333333"
                css={css`
                  padding: 4px 8px;
                `}
              >
                <TooltipText>Delete Contact</TooltipText>
              </ReactTooltip>
            </IconButtonWithHover>
          </ItemToolbarContainer>
        )}
      />
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <StyledButton
          isLoading={isLoading}
          disable={!isValidEmail}
          onClick={async (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            const webhookValidation = validateWebhook(webhook);
            if (webhookValidation.error) {
              const { toastText, toastType } =
                webhookValidation.error.toastInfo;
              toast(toastText, { type: toastType });
              return;
            }

            setIsLoading(true);

            try {
              const continueSendEmail = await handleCanSendTestEmail(
                selectedValue
              );

              if (continueSendEmail) {
                await handleSendTestWebhook();
                toast(`Webhook test sent successfully to ${selectedValue}`, {
                  type: "success",
                });
              } else {
                modal.pushModal(
                  <Warning
                    icon={"/icons/warning.svg"}
                    title="Create Contact"
                    description={`You can only send webhooks to contacts.Would you like to create a new one with ${selectedValue}?`}
                    buttonsName={["No", "Yes"]}
                    onOkClicked={() => {
                      modal.popModal();
                      modal.pushModal(
                        <AddContact emailFromTest={selectedValue} />,
                        "Add new contact"
                      );
                    }}
                    onCancelClicked={() => {
                      modal.popModal();
                    }}
                  />
                );
              }
            } catch (err) {
              toast(`Failed send webhook to ${selectedValue}`, {
                type: "error",
              });
            } finally {
              setIsLoading(false);
            }
          }}
          css={css`
            width: fit-content;
            height: 43px;
            padding: 16px;
            margin-top: 10px;
            flex-shrink: 0;
          `}
        >
          Send Test
        </StyledButton>
      </div>
    </TestPopover>
  );
};

export default WebhookTestPopover;
